import axios from "axios";
import { NewsFeedItem } from "./models";
import keycloakInstance from "@/initKeycloak.js";
import * as Sentry from "@sentry/react";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";
import { Portfolio } from "@/types/Portfolio";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import {
  extractKeywordsFromPortfolios,
  extractTickersFromPortfolios,
} from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import { WidgetAccessGroup } from "@/utils/widgets";

export const baseURL = import.meta.env.VITE_FIREHOSE_BASE_URL;

export const endpoints = {
  news: "/news",
  stream: "/stream",
  search: "/search/stories",

  // equities (datahose)
  streamData: "/stream-data",

  //stocks ratings
  stocksRatings: "/news/stocks-ratings",
  stocksRatingsStream: "/stream/stocks-ratings",
  stocksRatingsSearch: "/news/stocks-ratings/search",

  // datahose feeds
  insiderTransactionFeed: "/news-data?source=insider-transaction",
  governmentTradeFeed: "/news-data?source=government-trade",
  optionActivityFeed: "/news-data?source=option-activity",
  blockTradeFeed: "/news-data?source=block-trade",
  sec13fStocksFeed: "/news-data?source=newsquawk-sec-filings-stocks",

  // dedicated search endpoints
  searchOptionActivity: "/equities-data/option-activity",
  searchBlockTrades: "/equities-data/block-trades",
  searchGovTrades: "/equities-data/gov-trades",
  searchInsiderTransactions: "/equities-data/insider-transactions",
};

export const client = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // Refresh if expiring within 30s
      config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    } catch (error) {
      console.error("Token refresh error in interceptor:", error);
    }
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      Sentry.captureException(new Error("Firehose API returned 401"), {
        level: "warning",
        extra: {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
        },
      });
    }
    if (error.response) {
      console.error("Response error:", error.response.data);
    } else if (error.request) {
      console.error("Request error:", error.request);
    } else {
      console.error("General error:", error.message);
    }
    return Promise.reject(error);
  }
);

attachRetryOn401(client);

export const getFirehoseNewsFeed = async () => {
  const response = await client.get(endpoints.news);
  return response.data as NewsFeedItem[];
};

export interface SearchStoriesParams {
  q?: string | null;
  limit?: number;
  offset?: number;
  source?: (BackendSource | "allStories")[];
  portfolios?: Portfolio[];
  advanceFilter?: CategoryQuery;
  days?: number | null;
  signal?: AbortSignal;
  initialPage?: number;
  date_range?: { start_date?: string; end_date?: string };
}

export const getFirehoseFeedBySource = async (source: BackendSource) => {
  const data = (await client.get(`${endpoints.news}?source=${source}`))
    .data as NewsFeedItem[];
  return data.sort((a, b) => b.id - a.id);
};

export const searchStories = async (params: SearchStoriesParams) => {
  const allPortfolios = [
    ...(params.portfolios ?? []),
    ...(params.advanceFilter?.portfolios ?? []),
  ];

  const tickers = extractTickersFromPortfolios(allPortfolios);

  const searchKeywords = [
    params.q,
    ...extractKeywordsFromPortfolios(allPortfolios),
    params.advanceFilter?.searchKeyword,
  ];

  const response = await client.post(
    endpoints.search,
    {
      q: searchKeywords.filter((k): k is string => !!k).join(" OR "),
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
      source: params.source,
      tickers: tickers,
      days: params.days,
      filter_mode: (params.advanceFilter?.filterMode ?? "OR").toUpperCase(),
      keyword_op: (params.advanceFilter?.keywordOp ?? "OR").toUpperCase(),
      date_range: {
        start_date: params.date_range?.start_date,
        end_date: params.date_range?.end_date,
      },
    },
    { signal: params.signal }
  );
  return response.data?.results as NewsFeedItem[];
};

export const searchStocksRatings = async (params: SearchStoriesParams) => {
  const allPortfolios = [
    ...(params.portfolios ?? []),
    ...(params.advanceFilter?.portfolios ?? []),
  ];

  const tickers = extractTickersFromPortfolios(allPortfolios);

  const searchKeywords = [
    params.q,
    ...extractKeywordsFromPortfolios(allPortfolios),
    params.advanceFilter?.searchKeyword,
  ];

  const response = await client.post(
    endpoints.stocksRatingsSearch,
    {
      q: searchKeywords.filter((k): k is string => !!k).join(" OR "),
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
      tickers: tickers,
      days: params.days,
      filter_mode: (params.advanceFilter?.filterMode ?? "OR").toUpperCase(),
      keyword_op: (params.advanceFilter?.keywordOp ?? "OR").toUpperCase(),
      date_range: params.date_range,
    },
    { signal: params.signal }
  );
  return response.data?.results as NewsFeedItem[];
};

export const backendSources = [
  "stocks-one",
  "stocks-two",
  "stocks-three",
  "crypto-web-news",
  "fin-web-news",
  "energy-web-news",
  "truth-social",
  "fin-x",
  "energy-x",
  "stocks-news",
  "stocks-four",
  "stocks-four-filings",
  "stocks-four-fedspeak",
  "stocks-four-general",
  "newsquawk-sec-filings",
  "stocks-five-eu",
] as const;

export type BackendSource = (typeof backendSources)[number];

export const sourceLabels: Record<BackendSource, string> = {
  "stocks-one": "The Fly",
  "stocks-two": "Benzinga",
  "stocks-three": "Benzinga Long",
  "crypto-web-news": "Crypto Scraper",
  "fin-web-news": "Web Scraper",
  "energy-web-news": "Energy Scraper",
  "truth-social": "Truth Scraper",
  "fin-x": "Fin X (Twitter)",
  "stocks-news": "Equities News",
  "energy-x": "Energy X (Twitter)",
  "stocks-four": "Trade Exchange",
  "stocks-four-filings": "Trade Exchange Filings",
  "stocks-four-fedspeak": "Trade Exchange Fed Speak",
  "stocks-four-general": "Trade Exchange General",
  "newsquawk-sec-filings": "SEC Filings",
  "stocks-five-eu": "Financial Reports EU",
};

/** Internal-only sources — never visible to external users regardless of role */
export const internalOnlySources: BackendSource[] = [
  "energy-web-news",
  "fin-web-news",
  "stocks-five-eu",
];

/** Maps sources to the role required to access them. Unmapped = accessible to all. */
export const sourceAccessGroup: Partial<
  Record<BackendSource, WidgetAccessGroup>
> = {
  "stocks-one": "equities-suite-widgets",
  "stocks-two": "equities-suite-widgets",
  "stocks-three": "equities-suite-widgets",
  "stocks-four": "equities-suite-widgets",
  "stocks-four-filings": "equities-suite-widgets",
  "stocks-four-fedspeak": "equities-suite-widgets",
  "stocks-four-general": "equities-suite-widgets",
  "stocks-news": "equities-suite-widgets",
  "newsquawk-sec-filings": "equities-suite-widgets",
};

/** Pure function: check if a single source is accessible */
export const isSourceAccessible = (
  source: BackendSource,
  roles: string[],
  isInternalUser: boolean
): boolean => {
  if (isInternalUser) return true;
  if (internalOnlySources.includes(source)) return false;
  const requiredGroup = sourceAccessGroup[source];
  if (!requiredGroup) return true;
  return roles.includes(requiredGroup);
};

// Data Events Search
export type {
  SearchDataEventsParams,
  NumericFilter,
  EnumFilter,
} from "@/widget-components/equities-widgets/types";
import type { SearchDataEventsParams } from "@/widget-components/equities-widgets/types";

export const searchDataEvents = async (params: SearchDataEventsParams) => {
  const { signal, portfolios, tickers: explicitTickers, ...rest } = params;

  // Extract tickers from portfolios (same pattern as searchStories)
  const portfolioTickers = extractTickersFromPortfolios(portfolios ?? []);

  // Combine explicit tickers with portfolio-extracted tickers
  const allTickers = Array.from(
    new Set([...(explicitTickers ?? []), ...portfolioTickers])
  );

  const response = await client.post(
    "/search/data-events",
    {
      ...rest,
      tickers: allTickers.length > 0 ? allTickers : undefined,
      backend_source: [params.backend_source], // API expects array
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    },
    { signal }
  );
  return response.data?.results;
};

// --- Dedicated search endpoint functions ---

export interface DedicatedSearchParams {
  [key: string]: string | number | boolean | undefined;
}

const cleanParams = (params: DedicatedSearchParams) => {
  const cleaned: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== null) {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

export const searchOptionActivity = async (params: DedicatedSearchParams) => {
  const response = await client.get(endpoints.searchOptionActivity, {
    params: cleanParams(params),
  });
  return response.data?.results ?? response.data;
};

export const searchBlockTrades = async (params: DedicatedSearchParams) => {
  const response = await client.get(endpoints.searchBlockTrades, {
    params: cleanParams(params),
  });
  return response.data?.results ?? response.data;
};

export const searchGovTrades = async (params: DedicatedSearchParams) => {
  const response = await client.get(endpoints.searchGovTrades, {
    params: cleanParams(params),
  });
  return response.data?.results ?? response.data;
};

export const searchInsiderTransactions = async (params: DedicatedSearchParams) => {
  const response = await client.get(endpoints.searchInsiderTransactions, {
    params: cleanParams(params),
  });
  return response.data?.results ?? response.data;
};
